import React, { Component } from "react";
import {
  Container,
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Tabs,
  Tab,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { withFirebase } from "../Firebase";
import { compose } from "recompose";
import { NotificationManager } from "react-notifications";
import maplibregl from "maplibre-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "maplibre-gl/dist/maplibre-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import BusinessHoursEditor, { DEFAULT_BUSINESS_HOURS } from "./BusinessHoursEditor";

const styles = (theme) => ({
  root: {
    minHeight: "100vh",
    background: "#f5f5f5",
    padding: theme.spacing(4, 0),
  },
  container: {
    maxWidth: 1400,
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: "#0d47a1",
  },
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 350px",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  mapContainer: {
    position: "relative",
    height: "600px",
    borderRadius: "12px",
    overflow: "visible", // Alterado de hidden para visible
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  map: {
    width: "100%",
    height: "100%",
    borderRadius: "12px",
    overflow: "hidden",
  },
  sidebar: {
    padding: theme.spacing(3),
    borderRadius: "12px",
    maxHeight: "600px",
    overflow: "auto",
  },
  sectionTitle: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    color: "#0d47a1",
  },
  areaItem: {
    background: "#f9f9f9",
    borderRadius: "8px",
    marginBottom: theme.spacing(1),
    border: "1px solid #e0e0e0",
  },
  activeArea: {
    borderLeft: "4px solid #4caf50",
  },
  inactiveArea: {
    borderLeft: "4px solid #f44336",
  },
  dialogContent: {
    minWidth: 500,
    minHeight: 400,
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
  scheduleInfo: {
    fontSize: "0.85rem",
    color: "#666",
    display: "flex",
    alignItems: "center",
    marginTop: 4,
  },
  addButton: {
    marginTop: theme.spacing(2),
    background: "linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)",
    color: "white",
  },
  instructions: {
    background: "#e3f2fd",
    padding: theme.spacing(2),
    borderRadius: "8px",
    marginBottom: theme.spacing(3),
  },
});

// Centro padrão: Fortaleza, Brasil
const DEFAULT_CENTER = [-38.5434, -3.7172];
const DEFAULT_ZOOM = 12;

// Estilo customizado para Mapbox Draw compatível com MapLibre GL
// Corrige o erro de line-dasharray que precisa usar ["literal", [...]]
const customDrawStyles = [
  // Polígono preenchimento
  {
    id: "gl-draw-polygon-fill-inactive",
    type: "fill",
    filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
    paint: {
      "fill-color": "#3bb2d0",
      "fill-outline-color": "#3bb2d0",
      "fill-opacity": 0.1,
    },
  },
  {
    id: "gl-draw-polygon-fill-active",
    type: "fill",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
    paint: {
      "fill-color": "#fbb03b",
      "fill-outline-color": "#fbb03b",
      "fill-opacity": 0.1,
    },
  },
  // Contorno do polígono
  {
    id: "gl-draw-polygon-stroke-inactive",
    type: "line",
    filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#3bb2d0",
      "line-width": 2,
    },
  },
  {
    id: "gl-draw-polygon-stroke-active",
    type: "line",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#fbb03b",
      "line-width": 2,
    },
  },
  // Linhas
  {
    id: "gl-draw-line-inactive",
    type: "line",
    filter: ["all", ["==", "active", "false"], ["==", "$type", "LineString"], ["!=", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#3bb2d0",
      "line-width": 2,
    },
  },
  {
    id: "gl-draw-line-active",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "active", "true"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#fbb03b",
      "line-width": 2,
    },
  },
  // Pontos/vértices
  {
    id: "gl-draw-polygon-and-line-vertex-stroke-inactive",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
    paint: {
      "circle-radius": 5,
      "circle-color": "#fff",
    },
  },
  {
    id: "gl-draw-polygon-and-line-vertex-inactive",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
    paint: {
      "circle-radius": 3,
      "circle-color": "#fbb03b",
    },
  },
  // Ponto médio (midpoint)
  {
    id: "gl-draw-polygon-midpoint",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
    paint: {
      "circle-radius": 3,
      "circle-color": "#fbb03b",
    },
  },
];

class DeliveryAreasManagement extends Component {
  constructor(props) {
    super(props);

    this.state = {
      areas: [],
      selectedArea: null,
      dialogOpen: false,
      dialogTab: 0, // 0 = Info básica, 1 = Horários
      areaName: "",
      areaActive: true,
      businessHours: DEFAULT_BUSINESS_HOURS,
      editingAreaId: null, // ID da área sendo editada (null = nova área)
      loading: true,
      mapLoaded: false,
    };

    this.mapContainer = React.createRef();
    this.map = null;
    this.draw = null;
  }

  componentDidMount() {
    this.initializeMap();
    this.loadDeliveryAreas();
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove();
    }
  }

  initializeMap = () => {
    // Inicializa mapa MapLibre GL com tiles OpenStreetMap (gratuito com ruas)
    this.map = new maplibregl.Map({
      container: this.mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: [
              "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
              "https://b.tile.openstreetmap.org/{z}/{x}/{y}.png",
              "https://c.tile.openstreetmap.org/{z}/{x}/{y}.png",
            ],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          },
        },
        layers: [
          {
            id: "osm",
            type: "raster",
            source: "osm",
            minzoom: 0,
            maxzoom: 19,
          },
        ],
      },
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    // Adiciona controles de navegação
    this.map.addControl(new maplibregl.NavigationControl(), "top-right");

    // Aguarda o mapa carregar para adicionar o Mapbox Draw
    this.map.on("load", () => {
      console.log("Mapa carregado com sucesso");
      
      // Inicializa Mapbox Draw sem controles visuais (usamos botões externos)
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: false,
          trash: false,
        },
        defaultMode: "simple_select",
        styles: customDrawStyles,
      });

      this.map.addControl(this.draw);

      // Escuta eventos de desenho
      this.map.on("draw.create", this.handleDrawCreate);
      this.map.on("draw.update", this.handleDrawUpdate);
      this.map.on("draw.delete", this.handleDrawDelete);

      // Marca mapa como carregado
      this.setState({ mapLoaded: true });

      // Carrega áreas existentes no mapa após o draw estar pronto
      if (this.state.areas.length > 0) {
        this.displayAreasOnMap(this.state.areas);
      }
    });
  };

  // Inicia modo de desenho de polígono
  startDrawingPolygon = () => {
    if (this.draw) {
      this.draw.changeMode("draw_polygon");
      NotificationManager.info("Clique no mapa para começar a desenhar. Clique no primeiro ponto para fechar o polígono.");
    } else {
      NotificationManager.error("Aguarde o mapa carregar completamente");
    }
  };

  loadDeliveryAreas = async () => {
    const { firebase } = this.props;
    const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

    if (!organizationId) {
      NotificationManager.error("ID da organização não configurado");
      this.setState({ loading: false });
      return;
    }

    try {
      const snapshot = await firebase.deliveryAreas(organizationId).once("value");
      const areasData = snapshot.val() || {};

      const areasArray = Object.keys(areasData).map((id) => ({
        id,
        ...areasData[id],
      }));

      this.setState({ areas: areasArray, loading: false });

      // Exibe áreas existentes no mapa
      this.displayAreasOnMap(areasArray);
    } catch (error) {
      console.error("Erro ao carregar áreas de entrega:", error);
      NotificationManager.error("Falha ao carregar áreas de entrega");
      this.setState({ loading: false });
    }
  };

  displayAreasOnMap = (areas) => {
    if (!this.map || !this.draw) return;

    // Limpa features existentes
    this.draw.deleteAll();

    // Adiciona cada área como uma feature
    areas.forEach((area) => {
      if (area.geojson && area.geojson.geometry) {
        const feature = {
          ...area.geojson,
          id: area.id,
          properties: {
            ...area.geojson.properties,
            name: area.name,
            active: area.active,
          },
        };

        this.draw.add(feature);
      }
    });
  };

  handleDrawCreate = (event) => {
    const feature = event.features[0];
    console.log("Polígono criado:", feature);

    // Abre diálogo para definir nome da área
    this.setState({
      dialogOpen: true,
      dialogTab: 0,
      selectedArea: feature,
      areaName: "",
      areaActive: true,
      businessHours: DEFAULT_BUSINESS_HOURS,
      editingAreaId: null,
    });
  };

  handleDrawUpdate = (event) => {
    const feature = event.features[0];
    console.log("Polígono atualizado:", feature);

    // Encontra a área no state e atualiza
    const areaToUpdate = this.state.areas.find((a) => a.id === feature.id);

    if (areaToUpdate) {
      this.updateAreaInFirebase(feature.id, {
        geojson: feature,
        updatedAt: Date.now(),
      });
    }
  };

  handleDrawDelete = (event) => {
    const feature = event.features[0];
    console.log("Polígono deletado:", feature);

    // Deleta do Firebase
    this.deleteArea(feature.id);
  };

  saveNewArea = async () => {
    const { areaName, areaActive, businessHours, selectedArea, editingAreaId } = this.state;
    const { firebase } = this.props;
    const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

    if (!areaName.trim()) {
      NotificationManager.error("Por favor, insira um nome para a área de entrega");
      return;
    }

    // Se estamos editando uma área existente
    if (editingAreaId) {
      try {
        await firebase.deliveryArea(organizationId, editingAreaId).update({
          name: areaName,
          active: areaActive,
          businessHours: businessHours,
          updatedAt: Date.now(),
        });

        // Atualiza state local
        this.setState((prevState) => ({
          areas: prevState.areas.map((area) =>
            area.id === editingAreaId
              ? { ...area, name: areaName, active: areaActive, businessHours }
              : area
          ),
          dialogOpen: false,
          areaName: "",
          editingAreaId: null,
        }));

        NotificationManager.success("Área de entrega atualizada com sucesso");
      } catch (error) {
        console.error("Erro ao atualizar área de entrega:", error);
        NotificationManager.error("Falha ao atualizar área de entrega");
      }
      return;
    }

    if (!selectedArea) {
      NotificationManager.error("Nenhuma área selecionada");
      return;
    }

    try {
      const newAreaRef = firebase.deliveryAreas(organizationId).push();
      const areaId = newAreaRef.key;

      const areaData = {
        name: areaName,
        active: areaActive,
        businessHours: businessHours,
        geojson: selectedArea,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      await newAreaRef.set(areaData);

      // Atualiza state local
      this.setState((prevState) => ({
        areas: [...prevState.areas, { id: areaId, ...areaData }],
        dialogOpen: false,
        areaName: "",
        selectedArea: null,
        businessHours: DEFAULT_BUSINESS_HOURS,
      }));

      // Atualiza o ID da feature no controle de desenho
      this.draw.delete(selectedArea.id);
      this.draw.add({ ...selectedArea, id: areaId });

      NotificationManager.success("Área de entrega criada com sucesso");
    } catch (error) {
      console.error("Erro ao salvar área de entrega:", error);
      NotificationManager.error("Falha ao salvar área de entrega");
    }
  };

  updateAreaInFirebase = async (areaId, updates) => {
    const { firebase } = this.props;
    const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

    try {
      await firebase.deliveryArea(organizationId, areaId).update(updates);

      // Atualiza state local
      this.setState((prevState) => ({
        areas: prevState.areas.map((area) =>
          area.id === areaId ? { ...area, ...updates } : area
        ),
      }));

      NotificationManager.success("Área de entrega atualizada");
    } catch (error) {
      console.error("Erro ao atualizar área de entrega:", error);
      NotificationManager.error("Falha ao atualizar área de entrega");
    }
  };

  toggleAreaActive = (areaId, currentActive) => {
    this.updateAreaInFirebase(areaId, {
      active: !currentActive,
      updatedAt: Date.now(),
    });
  };

  editArea = (area) => {
    // Zoom para a área no mapa
    const feature = this.draw.get(area.id);
    if (feature) {
      // Calcula bounds e ajusta o mapa
      const coordinates = feature.geometry.coordinates[0];
      const bounds = coordinates.reduce(
        (bounds, coord) => {
          return [
            [Math.min(bounds[0][0], coord[0]), Math.min(bounds[0][1], coord[1])],
            [Math.max(bounds[1][0], coord[0]), Math.max(bounds[1][1], coord[1])],
          ];
        },
        [
          [Infinity, Infinity],
          [-Infinity, -Infinity],
        ]
      );

      this.map.fitBounds(bounds, { padding: 50 });
    }

    // Abre diálogo de edição
    this.setState({
      dialogOpen: true,
      dialogTab: 0,
      areaName: area.name,
      areaActive: area.active,
      businessHours: area.businessHours || DEFAULT_BUSINESS_HOURS,
      editingAreaId: area.id,
      selectedArea: null,
    });
  };

  openScheduleDialog = (area) => {
    // Abre diálogo diretamente na aba de horários
    this.setState({
      dialogOpen: true,
      dialogTab: 1,
      areaName: area.name,
      areaActive: area.active,
      businessHours: area.businessHours || DEFAULT_BUSINESS_HOURS,
      editingAreaId: area.id,
      selectedArea: null,
    });
  };

  handleDialogTabChange = (event, newValue) => {
    this.setState({ dialogTab: newValue });
  };

  handleBusinessHoursChange = (newBusinessHours) => {
    this.setState({ businessHours: newBusinessHours });
  };

  deleteArea = async (areaId) => {
    const { firebase } = this.props;
    const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

    if (!window.confirm("Tem certeza que deseja excluir esta área de entrega?")) {
      return;
    }

    try {
      await firebase.deliveryArea(organizationId, areaId).remove();

      // Remove o polígono do mapa
      if (this.draw) {
        this.draw.delete(areaId);
      }

      // Atualiza state local
      this.setState((prevState) => ({
        areas: prevState.areas.filter((area) => area.id !== areaId),
      }));

      NotificationManager.success("Área de entrega excluída");
    } catch (error) {
      console.error("Erro ao excluir área de entrega:", error);
      NotificationManager.error("Falha ao excluir área de entrega");
    }
  };

  handleCloseDialog = () => {
    // Remove o polígono não salvo (apenas se for nova área)
    if (this.state.selectedArea && !this.state.editingAreaId) {
      this.draw.delete(this.state.selectedArea.id);
    }

    this.setState({
      dialogOpen: false,
      dialogTab: 0,
      areaName: "",
      selectedArea: null,
      editingAreaId: null,
      businessHours: DEFAULT_BUSINESS_HOURS,
    });
  };

  // Formata os horários de funcionamento para exibição na lista
  formatBusinessHours = (businessHours) => {
    if (!businessHours || !businessHours.schedule) {
      return "Sem horários definidos";
    }

    const { schedule } = businessHours;
    const enabledDays = Object.entries(schedule)
      .filter(([, config]) => config.enabled)
      .length;

    if (enabledDays === 0) {
      return "Sempre fechado";
    }

    // Verifica se domingo está habilitado
    const hasWeekend = schedule.sunday?.enabled || schedule.saturday?.enabled;
    
    if (enabledDays === 7) {
      return "Todos os dias";
    } else if (enabledDays >= 5 && !schedule.sunday?.enabled && !schedule.saturday?.enabled) {
      return "Seg-Sex";
    } else if (hasWeekend && enabledDays >= 6) {
      return "Seg-Sáb";
    }

    return `${enabledDays} dias/semana`;
  };

  render() {
    const { classes } = this.props;
    const { areas, dialogOpen, dialogTab, areaName, areaActive, businessHours, editingAreaId, loading, mapLoaded } = this.state;

    return (
      <div className={classes.root}>
        <Container className={classes.container}>
          <Box className={classes.header}>
            <Typography className={classes.title}>
              <span role="img" aria-label="mapa">🗺️</span> Gerenciamento de Áreas de Entrega
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Defina e gerencie suas áreas de cobertura de entrega
            </Typography>
          </Box>

          <Paper className={classes.instructions}>
            <Typography variant="body2">
              <strong>Como usar:</strong>
            </Typography>
            <Typography variant="body2">
              • Clique no botão "Desenhar Nova Área" ou no ícone de polígono no mapa
            </Typography>
            <Typography variant="body2">
              • Clique no mapa para criar os pontos do polígono
            </Typography>
            <Typography variant="body2">
              • Clique no primeiro ponto para fechar e salvar a área
            </Typography>
            <Typography variant="body2">
              • Use o botão de lixeira no mapa ou na lista para excluir áreas
            </Typography>
          </Paper>

          <Box mb={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={this.startDrawingPolygon}
              disabled={!mapLoaded}
              style={{ marginRight: 8 }}
            >
              {mapLoaded ? "✏️ Desenhar Nova Área" : "Carregando mapa..."}
            </Button>
          </Box>

          <div className={classes.mainGrid}>
            <Paper className={classes.mapContainer}>
              <div ref={this.mapContainer} className={classes.map} />
            </Paper>

            <Paper className={classes.sidebar}>
              <Typography className={classes.sectionTitle}>
                Áreas Ativas ({areas.filter((a) => a.active).length})
              </Typography>

              {loading ? (
                <Typography variant="body2">Carregando...</Typography>
              ) : areas.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Nenhuma área de entrega ainda. Desenhe sua primeira área no mapa!
                </Typography>
              ) : (
                <List>
                  {areas.map((area) => (
                    <ListItem
                      key={area.id}
                      className={`${classes.areaItem} ${
                        area.active ? classes.activeArea : classes.inactiveArea
                      }`}
                    >
                      <ListItemText
                        primary={area.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" component="span">
                              {area.active ? "Ativa" : "Inativa"}
                            </Typography>
                            <Typography variant="caption" className={classes.scheduleInfo}>
                              <ScheduleIcon style={{ fontSize: 14, marginRight: 4 }} />
                              {this.formatBusinessHours(area.businessHours)}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={area.active}
                              onChange={() =>
                                this.toggleAreaActive(area.id, area.active)
                              }
                              color="primary"
                              size="small"
                            />
                          }
                          label=""
                        />
                        <IconButton
                          edge="end"
                          aria-label="horários"
                          onClick={() => this.openScheduleDialog(area)}
                          size="small"
                          title="Configurar horários"
                        >
                          <ScheduleIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="editar"
                          onClick={() => this.editArea(area)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="excluir"
                          onClick={() => this.deleteArea(area.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </div>

          {/* Diálogo para configurar área de entrega */}
          <Dialog 
            open={dialogOpen} 
            onClose={this.handleCloseDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {editingAreaId ? `Editar Área: ${areaName}` : "Nova Área de Entrega"}
            </DialogTitle>
            <Tabs
              value={dialogTab}
              onChange={this.handleDialogTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Informações" />
              <Tab label="Horários" />
            </Tabs>
            <DialogContent className={classes.dialogContent}>
              {dialogTab === 0 && (
                <Box className={classes.tabPanel}>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Nome da Área"
                    type="text"
                    fullWidth
                    value={areaName}
                    onChange={(e) => this.setState({ areaName: e.target.value })}
                    placeholder="ex: Centro, Aldeota, Meireles"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={areaActive}
                        onChange={(e) =>
                          this.setState({ areaActive: e.target.checked })
                        }
                        color="primary"
                      />
                    }
                    label="Ativa (aceitando pedidos)"
                    style={{ marginTop: 16 }}
                  />
                </Box>
              )}
              {dialogTab === 1 && (
                <Box className={classes.tabPanel}>
                  <BusinessHoursEditor
                    value={businessHours}
                    onChange={this.handleBusinessHoursChange}
                    compact
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleCloseDialog} color="default">
                Cancelar
              </Button>
              <Button onClick={this.saveNewArea} color="primary" variant="contained">
                {editingAreaId ? "Salvar Alterações" : "Salvar Área"}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </div>
    );
  }
}

export default compose(withStyles(styles), withFirebase)(DeliveryAreasManagement);
